import i18nInit from '@/configs/i18n';
import { create } from 'zustand';
import { storage } from '@/utils';
import { Locale } from 'antd/es/locale';
import { DEFAULT_LOCALE_LANG, Locales, STORAGE_KEY_LOCALE_LANG } from '@/configs';

import localeFA from 'antd/lib/locale/fa_IR';
import localeAR from 'antd/lib/locale/ar_EG';
import localeEN from 'antd/lib/locale/en_US';

interface LocaleStore {
  antLocale: Locale;
  dir: 'ltr' | 'rtl';
  fontFamily: string;
  loading: boolean;
  loaded: boolean;
  error: Error;
  locale: Locales;
}

interface LocaleStoreActions {
  load: () => void;
  setLocale: (value: Locales) => void;
}

const locale = storage.get(STORAGE_KEY_LOCALE_LANG, DEFAULT_LOCALE_LANG) as Locales;

export const useLocaleStore = create<LocaleStore & LocaleStoreActions>()((set, get) => ({
  dir: 'rtl',
  antLocale: localeFA,
  loading: false,
  loaded: false,
  locale,
  fontFamily: getFontFamily(locale),
  error: null,
  load: () => {
    const { locale } = get();
    set({ loading: true, error: null, loaded: false });
    i18nInit()
      .catch((e) => set({ loaded: true, error: e }))
      .finally(() => {
        set({
          antLocale: getAntLocale(locale),
          dir: getDirectionByLocale(locale),
          loading: false,
        });
      });
  },
  setLocale: (value: Locales) => {
    storage.set(STORAGE_KEY_LOCALE_LANG, value);
    set({ locale });
  },
}));

function getDirectionByLocale(locale: Locales) {
  if (locale.includes('fa') || locale.includes('ar')) return 'rtl';
  return 'ltr';
}

function getFontFamily(locale: Locales) {
  switch (locale) {
    case 'fa':
    case 'ar':
      return 'var(--app-font-sans-fa)';
    case 'en':
      return 'var(--app-font-sans-en)';
  }
  return 'var(--app-font-sans-fa)';
}

function getAntLocale(locale: string) {
  switch (locale) {
    case 'fa':
      return localeFA;
    case 'en':
      return localeEN;
    case 'ar':
      return localeAR;
  }
  return localeFA;
}

useLocaleStore.getState().load();

export default useLocaleStore;
